import { useState } from "react";
import { useNotification } from "@context/NotificationContext";
import type { SequenceView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { problemMessage, problemStatus } from "../utils/problemDetail";
import { useSequenceMutations } from "./useSequenceMutations";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;

export const useRemoveSequence = (operationId: string) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { remove } = useSequenceMutations(operationId);
  const [removing, setRemoving] = useState<SequenceView | null>(null);

  const confirm = async (onRemoved?: () => void): Promise<boolean> => {
    if (!removing) return false;
    try {
      await remove.mutateAsync(removing.id);
      onRemoved?.();
      return true;
    } catch (error) {
      const message = problemMessage(error);
      const rejected = problemStatus(error) === 409;
      notify({
        type: "error",
        title: rejected ? t(`${L}.notifications.change-rejected-title`) : t("common.error"),
        description: message ?? t(`${L}.notifications.change-failed`),
      });
      return false;
    } finally {
      setRemoving(null);
    }
  };

  return {
    removing,
    request: setRemoving,
    cancel: () => setRemoving(null),
    confirm,
    pending: remove.isPending,
  };
};
